import {
  ListFilterOption,
  TreeFilterKeys,
  TreeFilterMap,
  TreeInputData,
} from '../filter';
import { CustomerFiltersResponse } from '../customer/customer-model';

const traverseTreeData = (
  data: any[],
  filterKey: TreeFilterKeys
): TreeInputData[] =>
  data.map(({ id, name, subFolders = [], customerGroups = [] }) => {
    const hasChildren = subFolders?.length || customerGroups?.length;

    return {
      id,
      label: name,
      selected: !!hasChildren,
      ...(hasChildren
        ? {
            data: [
              ...traverseTreeData(subFolders, filterKey),
              ...customerGroups?.map((g: any) => ({
                id: g.id,
                label: g.name,
                selected: false,
                ...(g.emojiId ? { emojiId: g.emojiId } : {}),
              })),
            ],
          }
        : {}),
    };
  });

export const getTreeMappingFilters = (data: CustomerFiltersResponse) => {
  const treeMappings = [
    {
      data: data?.routes,
      filterkey: TreeFilterKeys.Routes,
    },
    {
      data: data?.customerSegment,
      filterkey: TreeFilterKeys.Segments,
    },
    {
      data: data?.dynamicSegment,
      filterkey: TreeFilterKeys.DynamicSegments,
    },
    {
      data: data?.customerLifeCycleGroups,
      filterkey: TreeFilterKeys.CustomerLifeCycle,
    },
  ];

  const treeFilter: Partial<TreeFilterMap> = {};
  treeMappings.forEach(({ data, filterkey }) => {
    if (data?.length) {
      const reqData = traverseTreeData(data, filterkey);
      const value =
        reqData.length === 1 && reqData[0]?.data ? reqData[0]?.data : reqData;

      treeFilter[filterkey] = {
        applyParent: false,
        value,
      };
    }
  });

  return { treeFilter };
};

export const collectSelectedTreeValueIds = (
  tree: TreeInputData[],
  result: string[] = []
): string[] => {
  tree.forEach((value) => {
    if (!value.data?.length && value.selected) {
      result.push(value.id);
    }
    if (value.data?.length) {
      collectSelectedTreeValueIds(value.data, result);
    }
  });

  return result;
};

export const resetTreeSelection = (tree: TreeInputData[]): TreeInputData[] => {
  return tree.map((value) => ({
    ...value,
    selected: value.isDefault ?? false,
    data: value.data ? resetTreeSelection(value.data) : [],
  }));
};

export const buildTreeValueMap = (
  tree: TreeInputData[],
  valuePath: Record<string, string> = {},
  parentPath = ''
) => {
  tree.forEach((value) => {
    const currentPath = parentPath
      ? `${parentPath}#${value.label}`
      : value.label;

    if (!value.data?.length) {
      valuePath[value.id] = currentPath;
    }
    if (value.data?.length) {
      buildTreeValueMap(value.data, valuePath, currentPath);
    }
  });

  return valuePath;
};

export const filterTreeBySearch = (
  nodes: TreeInputData[],
  search: string
): TreeInputData[] => {
  if (!search) return nodes;

  const lowerSearch = search.toLowerCase();

  return nodes.reduce<TreeInputData[]>((acc, node) => {
    const isMatch = node.label?.toLowerCase().includes(lowerSearch);
    let filteredChildren: TreeInputData[] = [];

    if (node.data?.length) {
      filteredChildren = filterTreeBySearch(node.data, search);
    }

    // ✅ if parent matches → keep full subtree
    if (isMatch) {
      acc.push(node);
      return acc;
    }

    // ✅ if child matches → keep only filtered children
    if (filteredChildren.length) {
      acc.push({
        ...node,
        data: filteredChildren,
      });
    }

    return acc;
  }, []);
};

export const filterListBySearch = (
  values: ListFilterOption['values'],
  search: string
) => {
  if (!search) return values;

  const lowerSearch = search.toLowerCase();

  return values.filter((item) =>
    item.label?.toLowerCase().includes(lowerSearch)
  );
};
